/**
 * Decides which in-flight request is still allowed to write to page state (CTX-276).
 *
 * The BGP Visibility page issues one request per selected entity. When the selection changes while
 * a request is still outstanding, the *newest* request must win: the outgoing one is aborted and,
 * even if it settles anyway (abort is not instantaneous, and mocked/cached transports resolve
 * regardless), it must not touch page state.
 *
 * Without this rule a reflector carrying no document could land its empty result on the canvas of
 * the reflector the user actually selected — and, because nothing else was scheduled, leave it
 * stranded there until a reload.
 *
 * Both per-entity requests on the page (the CPR document and the neighbor roster) own their state
 * through this same helper, so the two can never disagree about which reflector is on screen.
 */
export interface OwnedRequest {
  /** Handed to the HTTP layer so a superseded request stops work as early as it can. */
  signal: AbortSignal;
  /** True only while this is still the newest request — the sole condition for writing state. */
  isCurrent: () => boolean;
}

export interface RequestOwnership {
  /** Supersedes (and aborts) any in-flight request, returning the new one's handle. */
  claim: () => OwnedRequest;
  /** Aborts what is in flight without claiming a replacement — e.g. on unmount. */
  abortPending: () => void;
}

export function createRequestOwnership(): RequestOwnership {
  let currentId = 0;
  let pending: AbortController | null = null;

  return {
    claim: () => {
      pending?.abort();
      pending = new AbortController();

      const id = ++currentId;
      return {
        signal: pending.signal,
        isCurrent: () => id === currentId,
      };
    },

    abortPending: () => {
      pending?.abort();
      pending = null;
      // Advance the id as well, so a request that settles after this point reports itself stale
      // rather than writing to state that no longer exists.
      currentId++;
    },
  };
}
